"use client";

import * as React from "react";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "../cn";
import type { SelectOption } from "../select";

export type FilterComboboxProps = {
  /** Selected option value ("" = nothing selected → shows placeholder). */
  value: string;
  onValueChange: (v: string) => void;
  options: SelectOption[];
  /** Label shown on the trigger when nothing is selected. */
  placeholder: string;
  /** Placeholder for the search input inside the panel. */
  searchPlaceholder?: string;
  /**
   * Controlled search text. Provide together with `onSearchChange` to drive
   * server-side filtering; the panel then renders `options` as-is. Omit both
   * to let the combobox filter `options` client-side by label.
   */
  search?: string;
  onSearchChange?: (v: string) => void;
  /** Show a loading hint in the panel (e.g. while fetching server-side). */
  loading?: boolean;
  /** Message shown when there are no options to display. */
  emptyLabel?: string;
  className?: string;
  width?: string;
  icon?: React.ReactNode;
};

export const FilterCombobox = React.forwardRef<
  HTMLButtonElement,
  FilterComboboxProps
>(
  (
    {
      value,
      onValueChange,
      options,
      placeholder,
      searchPlaceholder = "Tìm...",
      search,
      onSearchChange,
      loading,
      emptyLabel = "Không có kết quả",
      className,
      width = "w-56",
      icon,
    },
    ref,
  ) => {
    const [open, setOpen] = React.useState(false);
    const [innerSearch, setInnerSearch] = React.useState("");
    const wrapRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const controlled = search !== undefined && onSearchChange !== undefined;
    const query = controlled ? (search ?? "") : innerSearch;

    const visible = React.useMemo(() => {
      if (controlled) return options;
      const q = innerSearch.trim().toLowerCase();
      if (!q) return options;
      return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [controlled, options, innerSearch]);

    const selected = options.find((o) => o.value === value);

    // Close on outside click.
    React.useEffect(() => {
      if (!open) return;
      function onDown(e: MouseEvent) {
        if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
      }
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    }, [open]);

    // Focus the search field when the panel opens.
    React.useEffect(() => {
      if (open) inputRef.current?.focus();
    }, [open]);

    function setQuery(v: string) {
      if (controlled) onSearchChange?.(v);
      else setInnerSearch(v);
    }

    function pick(v: string) {
      onValueChange(v === value ? "" : v);
      setOpen(false);
      setQuery("");
    }

    return (
      <div ref={wrapRef} className={cn("relative", width, className)}>
        <button
          ref={ref}
          type="button"
          onClick={() => setOpen((o) => !o)}
          className={cn(
            "group h-10 w-full rounded-[var(--radius-sm)] border-[1.5px]",
            "px-3 text-sm font-satoshi font-medium text-ink",
            "bg-mist border-smoke outline-none transition-all cursor-pointer",
            "focus:border-lime focus:bg-paper",
            "flex items-center justify-between gap-2",
            open && "border-lime bg-paper",
            value !== "" && "border-lime/60 bg-paper",
          )}
        >
          <div className="flex items-center gap-1.5 truncate">
            {icon ? <span className="text-ash">{icon}</span> : null}
            <span className={cn("truncate", value === "" && "text-ash")}>
              {selected?.label ?? placeholder}
            </span>
          </div>
          <ChevronDown
            size={16}
            strokeWidth={2}
            className={cn(
              "shrink-0 text-ash transition-transform duration-200",
              open && "rotate-180",
            )}
          />
        </button>

        {open ? (
          <div
            className={cn(
              "absolute z-50 mt-1.5 w-full overflow-hidden",
              "rounded-[var(--radius-md)] border border-smoke bg-paper",
              "shadow-[0_8px_24px_-4px_rgba(0,0,0,0.10),0_2px_8px_-2px_rgba(0,0,0,0.06)]",
            )}
          >
            <div className="flex items-center gap-2 border-b border-smoke px-3">
              <Search size={15} className="shrink-0 text-ash" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className={cn(
                  "h-10 w-full bg-transparent text-sm font-medium text-ink",
                  "outline-none placeholder:text-ash",
                )}
              />
            </div>
            <div className="max-h-64 overflow-y-auto p-1.5">
              {value !== "" ? (
                <button
                  type="button"
                  onClick={() => pick(value)}
                  className={cn(
                    "flex h-9 w-full items-center rounded-[var(--radius-sm)] px-3",
                    "text-sm font-medium text-ash outline-none cursor-pointer",
                    "transition-colors hover:bg-mist",
                  )}
                >
                  {placeholder}
                </button>
              ) : null}
              {loading ? (
                <p className="px-3 py-2 text-sm text-ash">Đang tải...</p>
              ) : visible.length === 0 ? (
                <p className="px-3 py-2 text-sm text-ash">{emptyLabel}</p>
              ) : (
                visible.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={opt.disabled}
                    onClick={() => pick(opt.value)}
                    className={cn(
                      "flex h-9 w-full items-center rounded-[var(--radius-sm)] px-3",
                      "text-sm font-medium text-ink outline-none cursor-pointer",
                      "transition-colors hover:bg-mist",
                      "disabled:opacity-40 disabled:cursor-not-allowed",
                      opt.value === value && "text-ember",
                    )}
                  >
                    <span className="truncate">{opt.label}</span>
                    {opt.value === value ? (
                      <Check
                        size={13}
                        strokeWidth={2.5}
                        className="ml-auto pl-2 text-ember"
                      />
                    ) : null}
                  </button>
                ))
              )}
            </div>
          </div>
        ) : null}
      </div>
    );
  },
);
FilterCombobox.displayName = "FilterCombobox";
